import { API_BASE_URL } from '../config/api';

// Helper to get auth token
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Get all conversations for a user
export const getConversations = async (userId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/messages/conversations/${userId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to fetch conversations');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching conversations:', error);
    throw error;
  }
};

// Get messages for a specific conversation
export const getMessages = async (conversationId, userId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/messages/conversations/${conversationId}/messages?userId=${userId}`,
      {
        headers: getAuthHeaders()
      }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch messages');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching messages:', error);
    throw error;
  }
};

// Send a message
// Note: Only Global Enterprise plan can send messages
// Basic and GCC plans cannot message anyone
export const sendMessage = async (conversationId, senderId, content, type = 'text', inquiryProducts = null) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/messages/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          senderId,
          content,
          type,
          inquiryProducts
        }),
      }
    );
    
    const data = await response.json();
    
    if (!response.ok) {
      // Check if it's a messaging permission error
      if (response.status === 403 && data.errorCode === 'MESSAGING_NOT_ALLOWED') {
        const error = new Error(data.message || 'Messaging is only available with Global Enterprise plan');
        error.code = 'MESSAGING_NOT_ALLOWED';
        error.requiredFeature = 'canLiveChat';
        throw error;
      }
      throw new Error(data.error || 'Failed to send message');
    }
    return data;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

// Create a new conversation (for inquiries)
// Note: Only Global Enterprise plan can create conversations/message
// Basic and GCC plans cannot message anyone
export const createConversation = async (buyerId, sellerId, type = 'inquiry', inquiryItems = null, initialMessage = null) => {
  try {
    console.log('Creating conversation:', { buyerId, sellerId, type, inquiryItems, initialMessage });
    
    const response = await fetch(`${API_BASE_URL}/messages/conversations`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        buyerId,
        sellerId,
        type,
        inquiryItems,
        initialMessage
      }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      console.error('Server error:', data);
      // Check if it's a messaging permission error
      if (response.status === 403 && data.errorCode === 'MESSAGING_NOT_ALLOWED') {
        const error = new Error(data.message || 'Messaging is only available with Global Enterprise plan');
        error.code = 'MESSAGING_NOT_ALLOWED';
        error.requiredFeature = 'canLiveChat';
        throw error;
      }
      throw new Error(data.error || 'Failed to create conversation');
    }
    
    return data;
  } catch (error) {
    console.error('Error creating conversation:', error);
    throw error;
  }
};

// Get unread message count
export const getUnreadCount = async (userId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/messages/unread/${userId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to fetch unread count');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching unread count:', error);
    throw error;
  }
};

// Mark conversation as read
export const markAsRead = async (conversationId, userId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/messages/conversations/${conversationId}/read`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ userId })
      }
    );
    if (!response.ok) {
      throw new Error('Failed to mark as read');
    }
    return await response.json();
  } catch (error) {
    console.error('Error marking as read:', error);
    throw error;
  }
};

// Delete conversation
export const deleteConversation = async (conversationId, userId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/messages/conversations/${conversationId}?userId=${userId}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders()
      }
    );
    if (!response.ok) {
      throw new Error('Failed to delete conversation');
    }
    return await response.json();
  } catch (error) {
    console.error('Error deleting conversation:', error);
    throw error;
  }
};

// Send inquiry from product inquiry sidebar
export const sendInquiry = async (buyerId, inquiryItems, message = '') => {
  try {
    console.log('Sending inquiry from buyerId:', buyerId);
    console.log('Inquiry items:', inquiryItems);
    
    // Group items by seller (using sellerId which should be a User ID)
    const itemsBySeller = {};
    inquiryItems.forEach(item => {
      // sellerId should be the user ID of the product owner
      const sellerId = item.sellerId;
      if (!sellerId) {
        console.warn('Item missing sellerId:', item);
        return; // Skip items without sellerId
      }
      if (!itemsBySeller[sellerId]) {
        itemsBySeller[sellerId] = [];
      }
      itemsBySeller[sellerId].push({
        id: item.id,
        name: item.description || item.name,
        image: item.image,
        price: `$${item.priceMin} - $${item.priceMax}`,
        unit: item.pricePer || item.unit,
        moq: item.moq,
        description: item.description || item.name
      });
    });

    console.log('Items grouped by seller:', itemsBySeller);

    if (Object.keys(itemsBySeller).length === 0) {
      throw new Error('No valid items to send. Products may be missing seller information.');
    }

    // Create a conversation for each seller
    const results = [];
    for (const [sellerId, items] of Object.entries(itemsBySeller)) {
      console.log(`Creating conversation with seller ${sellerId} for ${items.length} items`);
      const result = await createConversation(
        buyerId,
        parseInt(sellerId),
        'inquiry',
        items,
        message || `Inquiry for ${items.length} product(s)`
      );
      console.log('Conversation created:', result);
      results.push(result);
    }

    return results;
  } catch (error) {
    console.error('Error sending inquiry:', error);
    throw error;
  }
};

export default {
  getConversations,
  getMessages,
  sendMessage,
  createConversation,
  getUnreadCount,
  markAsRead,
  deleteConversation,
  sendInquiry
};
